import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Capacitor } from '@capacitor/core';

import Api from './ajax';
import storage from './utils/storage';
import { scheduleAlarms, setAcknowledgedCallback, registerAlarmListeners } from './components/alarms/alarmScheduler';
import { flushLogsOnForeground, syncAlarmLogsOnOpen } from './components/alarms/alarmSyncService';
import AlarmAckDialog from './components/alarms/AlarmAckDialog';
import { getSchoolHomeHash, isSchoolHomeDomain } from './utils/schoolHome';

import PageLoader from './components/common/PageLoader';
import ClasspepLoader from './components/common/ClasspepLoader';
import BootRetry from './components/common/BootRetry';
import secureTokenStorage from './utils/secureTokenStorage';
import { setCurrentUser } from './features/currentUser';
import { setSettings } from './features/settingsReducer';
import { setCheckAccess } from './features/checkAccess';

const WelcomeScreenDialog = lazy(() => import('./components/welcome-screen/WelcomeScreenDialog'));
const Login = lazy(() => import('./components/Login'));
const AppRoutes = lazy(() => import('./components/AppRoutes'));
const LoginIssue = lazy(() => import('./components/LoginIssue'));
const SMSNotifyPostProcess = lazy(() =>
  import('./components/sms-notify-post/Process'),
);
const OverAllView = lazy(() =>
  import('./components/sms-notify-post/OverAllView'),
);
const FeeCollectionForm = lazy(() =>
  import('./components/fee/FeeCollectionForm'),
);
const FeeOnlinePGWResponse = lazy(() =>
  import('./components/fee/FeeOnlinePGWResponse'),
);
const FeeCollectionStatusForm = lazy(() =>
  import('./components/fee/FeeCollectionStatusForm'),
);
const FeeCollectionStatusCheckForm = lazy(() =>
  import('./components/fee/FeeCollectionStatusCheckForm'),
);
const SvcPaymentStatus = lazy(() =>
  import('./components/service/SvcPaymentStatus'),
);
const SvcPublicPayScreen = lazy(() =>
  import('./components/service/SvcPublicPayScreen'),
);
const SurveyEntryForm = lazy(() =>
  import('./components/survey/SurveyEntryForm'),
);
const NoMenuRoutes = lazy(() => import('./components/NoMenuRoutes'));
const SimpleHomework = lazy(() =>
  import('./components/sms-notify-activity/SimpleHomework'),
);
const SchoolHomePage = lazy(() =>
  import('./components/SchoolHome/SchoolHomePage'),
);
const ShowcaseCategoryPage = lazy(() =>
  import('./components/SchoolHome/ShowcaseCategoryPage'),
);

const api = new Api();

const MainApp = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(null);
  const [fcmToken, setFcmToken] = useState(null);
  const [mobileDeviceInfo, setMobileDeviceInfo] = useState(null);
  const [showWelcomeScreen, setShowWelcomeScreen] = useState(false);
  const [welcomeCompleted, setWelcomeCompleted] = useState(false);
  const [appReady, setAppReady] = useState(false);
  // Startup could not reach the server — distinct from "not signed in".
  const [bootError, setBootError] = useState(false);

  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.currentUser.value);
  const alarmApiRef = useRef(new Api());
  const [ackDialog, setAckDialog] = useState({ open: false, title: '' });

  // Register notification listeners and the acknowledged callback once on mount
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    registerAlarmListeners();
    setAcknowledgedCallback((notification) => {
      setAckDialog({ open: true, title: notification?.title || '' });
    });
  }, []);

  // Re-schedule alarms and sync logs whenever the app comes back to foreground
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const scholarId = currentUser?.scholar_id;

    if (!scholarId) return;

    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;

      // Immediately detect fired alarms, store in localStorage, flush to server and clear.
      // This runs before the alarm list loads so no delivery log is ever lost.
      flushLogsOnForeground(alarmApiRef.current, scholarId);

      alarmApiRef.current.post('/alarm/list', { scholar_id: scholarId }, async (res) => {
        const alarms = res.s && res.d?.list?.length > 0 ? res.d.list : [];

        if (alarms.length > 0) await scheduleAlarms(alarms);
        // Always updates last_sync_time on the server, even when alarms is empty.
        syncAlarmLogsOnOpen(alarmApiRef.current, alarms, scholarId);
      });
    };

    // Call once immediately so sync runs on initial app open (visibilitychange never fires when already visible)
    handleVisibility();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [currentUser?.scholar_id]);

  useEffect(() => {
    let isMounted = true;
    const loadAndAuth = async () => {
      // Use async storage for Capacitor/iOS
      const [instId, scholarId] = await Promise.all([
        storage.getAsync('inst_id'),
        storage.getAsync('scholar_id'),
      ]);

      if (!isMounted) return;

      api.post(
        '/has-auth',
        {
          inst_id: instId,
          scholar_id: scholarId,
          roll_type: process.env.REACT_APP_ROLE || 'SCHOLAR'
        },
        async (res) => {
          setIsLoggedIn(res.s);
          if (res.s) {
            api.post(
              '/fee-balance',
              {
                scholar_id: res.d.current_user.scholar_id,
              },
              (res2) => {
                if (res2.s) {
                  storage.setMultiple({
                    BlockAccess: res2.d.BlockAccess,
                    AppAccess: res2.d.AppAccess,
                  });
                  dispatch(setCheckAccess(res2.d));
                }
              },
            );
            dispatch(setCurrentUser(res.d.current_user));

            storage.setMultiple({
              inst_id: res.d.current_user.inst_id,
              scholar_id: res.d.current_user.scholar_id,
              default_path: res.d.current_user.default_path,
              hf_color: JSON.stringify(res.d.current_user.hf_color),
            });

            // Only show welcome screen if not already completed today (avoids running on every kill+reopen)
            const today = new Date().toISOString().split('T')[0];
            const lastWelcomeDate = localStorage.getItem('last_welcome_sequence_date');
            
            if (lastWelcomeDate !== today) {
              setShowWelcomeScreen(true);
              setWelcomeCompleted(false);
              setAppReady(false);
            } else {
              setShowWelcomeScreen(false);
              setWelcomeCompleted(true);
              setAppReady(true);
            }
          } else if (res.f === 1006 || res.f === '1006') {
            // secureApi already tried to re-select this scholar and failed, so the
            // link really is gone and the user has to sign in again. Clear only the
            // stale session/selection keys — wiping all of storage here also took
            // out unrelated local data on what is often a recoverable condition.
            secureTokenStorage.clearTokens();
            ['inst_id', 'scholar_id', 'menu_id', 'default_path', 'scholar-token'].forEach((key) =>
              storage.remove(key),
            );

            let registrations =
              await navigator.serviceWorker.getRegistrations();

            for (let ii = 0; ii < registrations.length; ii++)
              registrations[ii].unregister();
            window.location.reload(true);
          } else {
            let registrations =
              await navigator.serviceWorker.getRegistrations();

            for (let ii = 0; ii < registrations.length; ii++)
              registrations[ii].unregister();
          }
        },
        // Transport failure (offline, 5xx, timeout). Without this the callback
        // above never runs, isLoggedIn stays null and the app sits on the splash
        // loader forever. A dropped connection is not an authentication failure:
        // keep the tokens and let the user retry.
        (err) => {
          console.warn('AUTH_EVENT event=has_auth_unreachable reason=transport source=MainApp.loadAndAuth', err?.reason || err?.message);
          setBootError(true);
        },
      );
    };

    loadAndAuth();

    return () => {
      isMounted = false;
    };
  }, [dispatch]);

  useEffect(() => {
    // Only handle location changes after app is ready (welcome completed)
    if (appReady && welcomeCompleted) {
      const blockAccess = storage.get('BlockAccess');

      if (blockAccess === 'Y') {
        storage.set('location.pathname', '/fee-overdue-landing');
        onLocationChange('/fee-overdue-landing');
      } else {
        // Use stored default_path or fallback to communication screen after welcome
        const storedDefaultPath = storage.get('default_path') || '/communication';
        const currentPath = window.location.pathname;


        // Navigate to default path if on root, sign-in, or if current path is not a valid route
        const shouldUseDefaultPath = currentPath === '/' || currentPath === '/sign-in' || currentPath === '/signin-issue';

        const targetPath = shouldUseDefaultPath ? storedDefaultPath : currentPath;

        storage.set('location.pathname', targetPath);
        onLocationChange(targetPath);
      }
    }
  }, [appReady, welcomeCompleted]);

  function onLocationChange(loc) {
    if (!(loc === '/sign-in' || loc === '/signin-issue')) {
      storage.set('location.pathname', loc);
    }
  }

  useEffect(() => {
    window.addEventListener('native-fcm-token', (e) => {
      const { token, deviceInfo } = e.detail;

      if (token) {
        setFcmToken(token);
      }

      if (deviceInfo) {
        setMobileDeviceInfo(deviceInfo);
      }
    });

    return () => window.removeEventListener('native-fcm-token', () => { });
  }, []);

  useEffect(() => {
    if (isLoggedIn && fcmToken && mobileDeviceInfo) {
      const saveToken = () => {
        api.post('/fcm-register-v2', {
          token: fcmToken,
          deviceInfo: mobileDeviceInfo,
        });
      };

      saveToken();
    }
  }, [isLoggedIn, fcmToken, mobileDeviceInfo]);

  const handleWelcomeComplete = () => {
    setShowWelcomeScreen(false);
    setWelcomeCompleted(true);
    setAppReady(true);

    setTimeout(() => {
      const defaultPath = storage.get('default_path') || '/communication';

      onLocationChange(defaultPath);
    }, 100);
  };

  useEffect(() => {
    if (!isLoggedIn) return;
    api.post('/get-settings', {}, (res) => { if (res.d.settings) dispatch(setSettings(res.d.settings)); });
  }, [isLoggedIn]);

  function isDisableMenu() {
    const cPath = window.location.pathname;

    if (cPath.indexOf('/fee-dismenu/online-pgw') !== -1) return true;

    return false;
  }

  let routeComponents;

  if (bootError && isLoggedIn === null) {
    // Could not reach the server at startup. Show a retry instead of an endless
    // splash loader, and above all do not treat this as a signed-out state — the
    // session is still valid and will work as soon as the network comes back.
    routeComponents = <BootRetry onRetry={() => window.location.reload()} />;
  } else if (isLoggedIn === null) {
    routeComponents = <ClasspepLoader />;
  } else {
    if (!isLoggedIn) {
      routeComponents = (
        <BrowserRouter>
          <Routes>
            <Route
              path="sign-in"
              element={
                <Suspense fallback={<ClasspepLoader />}>
                  <Login
                    authenticate={() => {
                      setIsLoggedIn(true);
                      setShowWelcomeScreen(true);
                      setWelcomeCompleted(false);
                      setAppReady(false);
                    }}
                  />
                </Suspense>
              }
            />
            <Route
              path="signin-issue"
              element={
                <Suspense fallback={<ClasspepLoader />}>
                  <LoginIssue />
                </Suspense>
              }
            />
            <Route
              path="s"
              element={
                <Suspense fallback={<ClasspepLoader />}>
                  <SMSNotifyPostProcess />
                </Suspense>
              }
            />
            <Route
              path="sms-notify-post/:hash"
              element={
                <Suspense fallback={<ClasspepLoader />}>
                  <OverAllView />
                </Suspense>
              }
            />

            {/* Task 1 — SVC payment status page (public, post-PineLabs redirect) */}
            <Route path="p/service">
              <Route path="online-pgw">
                <Route
                  path="status/:hash/:hash1"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <SvcPaymentStatus mode="P" />
                    </Suspense>
                  }
                />
              </Route>
              {/* mirrors p/fee/coll/:hash — app_type='I' */}
              <Route
                path="coll/:pub_key"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <SvcPublicPayScreen mode="I" />
                  </Suspense>
                }
              />
            </Route>

            {/* Anonymous survey — fully public, no login, mirrors p/service/coll/:pub_key */}
            <Route path="p/survey">
              <Route
                path=":pubKey"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <SurveyEntryForm />
                  </Suspense>
                }
              />
            </Route>

            <Route path="p/fee">
              <Route
                path="coll/:hash"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <FeeCollectionForm mode="I" />
                  </Suspense>
                }
              />
              <Route path="online-pgw">
                <Route
                  path="response"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <FeeOnlinePGWResponse mode="I" />
                    </Suspense>
                  }
                ></Route>
                <Route
                  path="status/:hash/:hash1"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <FeeCollectionStatusForm mode="P" />
                    </Suspense>
                  }
                ></Route>
                <Route
                  path="status-check/:hash/:hash1"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <FeeCollectionStatusCheckForm mode="P" />
                    </Suspense>
                  }
                ></Route>
              </Route>
            </Route>
            <Route
              path="sms-notify-activity/:hash"
              element={
                <Suspense fallback={<ClasspepLoader />}>
                  <SimpleHomework />
                </Suspense>
              }
            />

            {isSchoolHomeDomain() && (
              <>
                <Route
                  path="school-home/:instHash"
                  element={
                    <Suspense fallback={<ClasspepLoader />}>
                      <SchoolHomePage />
                    </Suspense>
                  }
                />
                <Route
                  path="school-home/:instHash/showcase/:catId"
                  element={
                    <Suspense fallback={<ClasspepLoader />}>
                      <ShowcaseCategoryPage />
                    </Suspense>
                  }
                />
              </>
            )}

            <Route path="*" element={
              getSchoolHomeHash()
                ? <Navigate to={`/school-home/${getSchoolHomeHash()}`} />
                : <Navigate to="/sign-in" />
            } />
          </Routes>
        </BrowserRouter>
      );
    } else {
      if (showWelcomeScreen && !welcomeCompleted) {
        routeComponents = (
          <BrowserRouter>
            <Suspense fallback={<ClasspepLoader />}>
              <WelcomeScreenDialog
                isLoggedIn={isLoggedIn}
                currentUser={currentUser}
                onWelcomeComplete={handleWelcomeComplete}
              />
            </Suspense>
          </BrowserRouter>
        );
      } else if (!appReady || !welcomeCompleted) {
        routeComponents = <ClasspepLoader />; // Show loader until app is ready
      } else if (isDisableMenu()) {
        routeComponents = (
          <Suspense fallback={<ClasspepLoader />}>
            <NoMenuRoutes onLocationChange={onLocationChange} />
          </Suspense>
        );
      } else {
        routeComponents = (
          <Suspense fallback={<ClasspepLoader />}>
            <AppRoutes onLocationChange={onLocationChange} />
          </Suspense>
        );
      }
    }
  }

  return (
    <>
      {routeComponents}
      <AlarmAckDialog
        open={ackDialog.open}
        title={ackDialog.title}
        onClose={() => setAckDialog({ open: false, title: '' })}
      />
    </>
  );
};

export default MainApp;
